import { useEffect, useRef, useState } from 'react'
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter'
import { oneDark } from 'react-syntax-highlighter/dist/esm/styles/prism'
import type { Algorithm, InputField, Problem, RunState, StageStatus } from '../types'
import type { BeginnerCfg } from '../data/beginner'
import { PREP_NOTES, SAMPLES, WHY_HERE } from '../data/story'
import { downloadProgram, fullProgram, STATIC_BUNDLES } from './CodeViewer'
import { shareWin, winMessage } from '../lib/share'

// Story workbench: left = the narrative (problem → why this model → the six
// pipeline stages, elaborated with sample data), right = sliders + RUN +
// result. Stage tabs light up live as the real pipeline streams over SSE.

interface Props {
  problem: Problem
  algo: Algorithm
  cfg: BeginnerCfg
  inputs: Record<string, number | string>
  run: RunState
  hasRun: boolean // ≥1 completed run for this problem since opening
  isSolved: boolean
  justWon: boolean
  height: number
  onDragStart: (e: React.PointerEvent) => void
  onSwitchAlgo: (id: string) => void
  onInputChange: (name: string, value: number | string) => void
  onRun: () => void
  onClose: () => void
}

// ---- big number count-up ----
function useCountUp(target: string | null) {
  const [text, setText] = useState<string | null>(null)
  const raf = useRef(0)
  useEffect(() => {
    cancelAnimationFrame(raf.current)
    if (target === null) {
      setText(null)
      return
    }
    const num = parseFloat(target.replace(/[^0-9.]/g, ''))
    if (!isFinite(num)) {
      setText(target) // non-numeric headline (e.g. crop label)
      return
    }
    const t0 = performance.now()
    const tick = (t: number) => {
      const k = Math.min(1, (t - t0) / 600)
      const n = num * (0.4 + 0.6 * k * k)
      const rendered = target.replace(/[0-9][0-9,.]*/, () =>
        num % 1 === 0 ? Math.round(n).toLocaleString('en-US') : n.toFixed(1),
      )
      setText(k < 1 ? rendered : target)
      if (k < 1) raf.current = requestAnimationFrame(tick)
    }
    raf.current = requestAnimationFrame(tick)
    return () => cancelAnimationFrame(raf.current)
  }, [target])
  return text
}

function fieldFmt(f: InputField, v: number | string): string {
  if (f.type === 'select') return String(v)
  const n = Number(v)
  if (/year/i.test(f.label)) return String(n) // 2005, not 2,005
  const s = n % 1 === 0 ? n.toLocaleString('en-US') : n.toFixed(1)
  if (f.unit === '$') return '$' + s
  return f.unit ? `${s} ${f.unit}` : s
}

const STATUS_TAB: Record<StageStatus, string> = {
  idle: 'text-mut hover:bg-ink-800 hover:text-white',
  running: 'bg-accent-500/25 text-accent-300 shadow-glow-accent',
  done: 'text-brand-300',
}

// pseudo-stages: the intro before "1 Dataset", the full program after "6 Predict"
const PROBLEM_TAB = 'problem'
const FULL_TAB = 'fullprogram'

export default function Drawer(props: Props) {
  const {
    problem, algo, cfg, inputs, run, hasRun, isSolved, justWon,
    height, onDragStart, onSwitchAlgo, onInputChange, onRun, onClose,
  } = props
  const [stageId, setStageId] = useState<string>(PROBLEM_TAB)
  const [shareFeedback, setShareFeedback] = useState<string | null>(null)
  const shareT = useRef(0)
  useEffect(() => () => window.clearTimeout(shareT.current), [])

  // opening / switching algorithm starts on the problem intro
  useEffect(() => {
    setStageId(PROBLEM_TAB)
  }, [algo.id])

  const running = run.status === 'running'
  const result = run.result

  // while the pipeline runs, follow the stage that is executing;
  // when it finishes, land on Predict — that's where the answer lives
  useEffect(() => {
    if (run.status === 'done') {
      setStageId(algo.pipeline[algo.pipeline.length - 1].id)
      return
    }
    if (run.status !== 'running') return
    const active = algo.pipeline.find((s) => run.stageStatus[s.id] === 'running')
    if (active) setStageId(active.id)
  }, [run.status, run.stageStatus, algo.pipeline])

  const onProblemTab = stageId === PROBLEM_TAB
  const stage = algo.pipeline.find((s) => s.id === stageId) ?? algo.pipeline[0]
  const stageKind = stage.id.split('-').pop() ?? ''
  const sample = stageKind === 'dataset' ? SAMPLES[problem.id] : null
  const prepNote = stageKind === 'preprocess' ? PREP_NOTES[problem.id] : null

  // headline for the big number: % for yes/no questions, value otherwise
  let big: string | null = null
  let bigSub = ''
  let bigRisk = false
  if (result) {
    if (result.kind === 'classification' && result.probability !== undefined) {
      big = Math.round(result.probability * 100) + '%'
      bigRisk = result.probability > 0.5
      bigSub = bigRisk ? `chance of “${result.label}”` : `safe — “${result.label}”`
    } else {
      big = result.headline
      bigSub = result.detail
    }
  }
  // fall back to the final value if no animation frame has fired yet
  // (e.g. hidden tab, where requestAnimationFrame is paused)
  const shown = useCountUp(big) ?? big
  const drivers = result
    ? [...result.featureImportance].sort((a, b) => b.value - a.value)
    : []

  const onShare = async () => {
    const outcome = await shareWin(winMessage(cfg.emoji, cfg.question, big))
    if (outcome === 'shared') return // native sheet gave its own feedback
    setShareFeedback(outcome === 'copied' ? '✓ copied — paste it anywhere' : 'copy failed')
    window.clearTimeout(shareT.current)
    shareT.current = window.setTimeout(() => setShareFeedback(null), 2400)
  }

  return (
    <div
      className="flex shrink-0 flex-col border-t border-ink-600 bg-ink-900/[.97]"
      style={{ height }}
    >
      {/* drag handle — resize the split between map and drawer */}
      <div
        onPointerDown={onDragStart}
        title="Drag to resize"
        className="group flex h-2.5 shrink-0 cursor-row-resize items-center justify-center bg-transparent hover:bg-accent-500/10"
      >
        <div className="h-1 w-14 rounded-full bg-ink-600 transition-colors group-hover:bg-accent-500" />
      </div>
      {/* header */}
      <div className="flex shrink-0 items-center justify-between border-b border-ink-700 px-5 py-2">
        <div className="flex items-center gap-3">
          <span className="text-[17px] font-extrabold">
            <span className="mr-2">{cfg.emoji}</span>
            {problem.title}
          </span>
          {problem.algorithms.length > 1 && (
            <span className="flex items-center gap-1 rounded-lg border border-ink-700 p-0.5">
              {problem.algorithms.map((a) => (
                <button
                  key={a.id}
                  onClick={() => onSwitchAlgo(a.id)}
                  className={
                    'rounded-md px-2.5 py-1 text-[13px] font-semibold transition-colors ' +
                    (a.id === algo.id
                      ? 'bg-accent-500/25 text-accent-300'
                      : 'text-mut hover:bg-ink-800 hover:text-white')
                  }
                >
                  {a.name}
                </button>
              ))}
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={onClose}
            className="rounded-md border border-ink-600 px-2.5 py-1 text-[13px] font-semibold text-mut hover:text-white"
          >
            ✕
          </button>
        </div>
      </div>

      {/* body */}
      <div className="grid min-h-0 flex-1 grid-cols-[1.35fr,0.75fr]">
        {/* left: the story */}
        <div className="min-w-0 overflow-auto border-r border-ink-700 px-6 py-4">
          <>
              {/* stage tabs — problem intro first, then the pipeline; light up live during a run */}
              <div className="flex flex-wrap items-center gap-1.5">
                <button
                  onClick={() => setStageId(PROBLEM_TAB)}
                  className={
                    'rounded-md px-2.5 py-1 text-[13px] font-semibold transition-colors ' +
                    (onProblemTab
                      ? 'bg-teal-500/25 text-teal-300'
                      : 'text-mut hover:bg-ink-800 hover:text-white')
                  }
                >
                  ◈ Problem
                </button>
                <div className="mx-0.5 h-4 w-px bg-ink-700" />
                {algo.pipeline.map((s, i) => {
                  const status: StageStatus = run.stageStatus[s.id] ?? 'idle'
                  const active = stageId === s.id
                  return (
                    <button
                      key={s.id}
                      onClick={() => setStageId(s.id)}
                      className={
                        'rounded-md px-2.5 py-1 text-[13px] font-medium transition-colors ' +
                        (active
                          ? 'bg-accent-500/25 text-accent-300'
                          : STATUS_TAB[status])
                      }
                    >
                      <span className="mr-1 opacity-60">{status === 'done' ? '✓' : i + 1}</span>
                      {s.label}
                    </button>
                  )
                })}
                <div className="mx-0.5 h-4 w-px bg-ink-700" />
                <button
                  onClick={() => setStageId(FULL_TAB)}
                  className={
                    'rounded-md px-2.5 py-1 text-[13px] font-semibold transition-colors ' +
                    (stageId === FULL_TAB
                      ? 'bg-fuchsia-500/25 text-fuchsia-300'
                      : 'text-mut hover:bg-ink-800 hover:text-white')
                  }
                >
                  {'</>'} Full program
                </button>
                <button
                  onClick={() => downloadProgram(algo)}
                  title={STATIC_BUNDLES.has(algo.id) ? 'Download the full project bundle (.zip)' : 'Download the complete program as a .py file'}
                  className="ml-auto rounded-md border border-accent-500/50 bg-accent-500/10 px-2.5 py-1 text-[13px] font-semibold text-accent-300 transition-colors hover:bg-accent-500/25"
                >
                  {STATIC_BUNDLES.has(algo.id) ? '⬇ Download .zip' : '⬇ Download .py'}
                </button>
              </div>

              {onProblemTab ? (
                <>
                  {/* problem intro + why this model */}
                  <div className="mt-4 grid grid-cols-2 gap-6">
                    <div>
                      <div className="text-[12px] font-semibold uppercase tracking-widest text-teal-400">
                        The problem
                      </div>
                      <h2 className="mt-1.5 text-[20px] font-semibold leading-snug text-white">
                        {problem.blurb}
                      </h2>
                      <p className="mt-2 text-[14.5px] leading-relaxed text-mut">
                        {problem.realWorld}
                      </p>
                      <div className="mt-3 flex flex-wrap gap-2">
                        <span className="rounded-md bg-ink-800 px-2.5 py-1 text-[13px] text-mut">
                          📊 {algo.dataset.name} · {algo.dataset.rows.toLocaleString('en-US')} rows ·{' '}
                          {algo.dataset.features} features
                        </span>
                        <span className="rounded-md bg-ink-800 px-2.5 py-1 text-[13px] text-mut">
                          🎯 {algo.sampleMetric.name} {algo.sampleMetric.value}
                        </span>
                      </div>
                    </div>
                    <div>
                      <div className="text-[12px] font-semibold uppercase tracking-widest text-accent-400">
                        Why {algo.name} here?
                      </div>
                      <p className="mt-1.5 text-[14.5px] leading-relaxed text-mut">
                        {WHY_HERE[algo.id] ?? algo.summary}
                      </p>
                    </div>
                  </div>
                  <p className="mt-5 text-[13.5px] leading-relaxed text-mut/80">
                    Walk the stages{' '}
                    <span className="font-medium text-accent-300">
                      1 Dataset → {algo.pipeline.length} Predict
                    </span>{' '}
                    to see how the solution is built, then press{' '}
                    <span className="font-medium text-accent-300">▶ RUN</span> to watch it execute
                    for real.
                  </p>
                </>
              ) : stageId === FULL_TAB ? (
                <>
                  <p className="mt-3 text-[13px] leading-relaxed text-mut">
                    The complete program — all {algo.pipeline.length} stages stitched together in
                    run order.{' '}
                    {STATIC_BUNDLES.has(algo.id)
                      ? 'Download the .zip to get the script, data generator, and README with setup instructions.'
                      : 'Download it and run it on your own laptop.'}
                  </p>
                  <div className="mt-3 rounded-xl border border-ink-700 bg-ink-950">
                    <SyntaxHighlighter
                      language="python"
                      style={oneDark}
                      showLineNumbers
                      lineNumberStyle={{ color: '#4a4a58', minWidth: '2.4em' }}
                      customStyle={{ margin: 0, background: 'transparent', fontSize: 13.5, padding: '12px 14px' }}
                      codeTagProps={{ style: { fontFamily: 'JetBrains Mono, ui-monospace, monospace' } }}
                    >
                      {fullProgram(algo)}
                    </SyntaxHighlighter>
                  </div>
                </>
              ) : (
                <>
                  {/* stage content: description + extras + code */}
                  <p className="mt-3 text-[14.5px] leading-relaxed text-mut">{stage.why}</p>

              {sample && (
                <div className="mt-3">
                  <table className="w-full border-collapse text-[13px]">
                    <thead>
                      <tr>
                        {sample.columns.map((c, i) => (
                          <th
                            key={c}
                            className={
                              'border-b border-ink-600 px-2 py-1.5 text-left font-semibold ' +
                              (i === sample.columns.length - 1 ? 'text-teal-300' : 'text-mut')
                            }
                          >
                            {c}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {sample.rows.map((row, ri) => (
                        <tr key={ri} className="odd:bg-ink-850/60">
                          {row.map((cell, ci) => (
                            <td
                              key={ci}
                              className={
                                'border-b border-ink-700/60 px-2 py-1.5 ' +
                                (ci === row.length - 1
                                  ? 'font-semibold text-teal-300'
                                  : 'text-white/90')
                              }
                            >
                              {cell}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  <p className="mt-1.5 text-[13px] text-mut">
                    {sample.note}{' '}
                    <span className="text-teal-300">
                      The last column is the answer the model learns to predict.
                    </span>
                  </p>
                </div>
              )}

              {prepNote && (
                <div className="mt-3 rounded-xl border border-teal-500/30 bg-teal-500/[.06] px-3.5 py-2.5 text-[13.5px] leading-relaxed text-mut">
                  <span className="font-semibold text-teal-300">What happens here: </span>
                  {prepNote}
                </div>
              )}

                  <div className="mt-3 rounded-xl border border-ink-700 bg-ink-950">
                    <SyntaxHighlighter
                      language={stage.language ?? 'python'}
                      style={oneDark}
                      customStyle={{ margin: 0, background: 'transparent', fontSize: 13.5, padding: '12px 14px' }}
                      codeTagProps={{ style: { fontFamily: 'JetBrains Mono, ui-monospace, monospace' } }}
                    >
                      {stage.code}
                    </SyntaxHighlighter>
                  </div>
                </>
              )}
            </>
        </div>

        {/* right: try it */}
        <div className="min-w-0 overflow-auto bg-ink-850 px-5 py-4">
          <div className="text-[12px] font-semibold uppercase tracking-widest text-mut">
            Try it yourself
          </div>
          {algo.inputs.map((f) => {
            const value = inputs[f.name] ?? f.default
            if (f.type === 'select' && f.options) {
              const idx = f.options.findIndex((o) => String(o) === String(value))
              return (
                <div key={f.name} className="mt-2.5">
                  <label className="flex justify-between text-[13.5px] text-mut">
                    {f.label}
                    <output className="font-bold text-white">{fieldFmt(f, value)}</output>
                  </label>
                  <input
                    type="range"
                    min={0}
                    max={f.options.length - 1}
                    step={1}
                    value={idx < 0 ? 0 : idx}
                    disabled={running}
                    onChange={(e) => onInputChange(f.name, String(f.options![Number(e.target.value)]))}
                    className="w-full accent-brand-500"
                  />
                </div>
              )
            }
            const r = cfg.ranges[f.name] ?? { min: 0, max: Number(f.default) * 3 || 100, step: 1 }
            return (
              <div key={f.name} className="mt-2.5">
                <label className="flex justify-between text-[13.5px] text-mut">
                  {f.label}
                  <output className="font-bold text-white">{fieldFmt(f, value)}</output>
                </label>
                <input
                  type="range"
                  min={r.min}
                  max={r.max}
                  step={r.step}
                  value={Number(value)}
                  disabled={running}
                  onChange={(e) => onInputChange(f.name, Number(e.target.value))}
                  className="w-full accent-brand-500"
                />
              </div>
            )
          })}

          <button
            onClick={onRun}
            disabled={running}
            className="mt-4 w-full rounded-2xl bg-gradient-to-r from-accent-600 to-fuchsia-600 py-3 text-[16px] font-extrabold text-white shadow-glow-accent transition hover:brightness-110 active:scale-[.98] disabled:cursor-not-allowed disabled:opacity-60"
          >
            {running ? 'running the real pipeline…' : hasRun ? '▶ RUN AGAIN' : '▶ RUN'}
          </button>

          {result && shown && (
            <div className="mt-4 rounded-2xl border border-ink-700 bg-ink-900 px-4 py-3 text-center">
              <div
                className={
                  'bg-gradient-to-r bg-clip-text text-[38px] font-extrabold leading-tight tracking-tight text-transparent ' +
                  (bigRisk ? 'from-red-400 to-orange-400' : 'from-brand-400 to-accent-400')
                }
              >
                {shown}
              </div>
              <div className="text-[13.5px] text-mut">
                {bigSub}
                {drivers.length > 0 && (
                  <>
                    {' · '}biggest reason:{' '}
                    <b className="font-semibold text-white">{drivers[0].name}</b>
                  </>
                )}
              </div>
              <div className="mt-2.5">
                {drivers.map((d, i) => (
                  <div key={d.name} className="mt-1 flex items-center gap-2 text-[12.5px] text-mut">
                    <span className="w-[100px] shrink-0 truncate text-right">{d.name}</span>
                    <span className="h-[8px] flex-1 overflow-hidden rounded-full bg-ink-700">
                      <i
                        className={
                          'block h-full rounded-full transition-all duration-500 ' +
                          (i === 0 ? 'bg-brand-500' : 'bg-[#5b6a94]')
                        }
                        style={{ width: `${Math.round((d.value / drivers[0].value) * 100)}%` }}
                      />
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {hasRun && !isSolved && !justWon && (
            <div className="mt-3 animate-popin rounded-xl border border-accent-500/50 bg-accent-500/10 px-3.5 py-2.5 text-[14px] font-bold text-accent-300">
              {cfg.challenge.text}
            </div>
          )}
          {(justWon || isSolved) && (
            <div className="mt-3 animate-popin rounded-xl border border-amber-400/50 bg-amber-400/10 px-3.5 py-2.5">
              <div className="text-[14.5px] font-bold text-amber-300">
                {justWon ? cfg.challenge.win : '✓ solved'}
              </div>
              <button
                onClick={onShare}
                className="mt-2 w-full rounded-lg border border-amber-400/40 py-1.5 text-[13.5px] font-bold text-amber-200 transition-colors hover:bg-amber-400/15"
              >
                {shareFeedback ?? '📣 challenge a friend'}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
